import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useUser } from "@/client/services/user";
import { buildCourseEnrollmentPayload } from "../utils/enrollment";

const API = "/api/integration/external-service/coding-platform/courses";
const unwrap = (r: any) => r?.data?.result ?? r?.data;

const toArray = (v: any): any[] => (Array.isArray(v) ? v : []);

export function useCourses() {
  const { user } = useUser();
  const studentId = user?.id ?? "";

  const [courses, setCourses] = useState<any[]>([]);
  const [facets, setFacets] = useState<any>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recommended, setRecommended] = useState<any[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<any[]>([]);
  const [bookmarks, setBookmarks] = useState<any[]>([]);
  const [filters, setFilters] = useState({ search: "", category: "", language: "", difficulty: "" });
  const [page, setPage] = useState(0);

  const fetchCourses = useCallback(async (f = filters, p = 0) => {
    if (!studentId) return;
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        studentId,
        page: p + 1,
        limit: 12,
      };
      if (f.search) params.search = f.search;
      if (f.category) params.category = f.category;
      if (f.language) params.programmingLanguage = f.language;
      if (f.difficulty) params.difficulty = f.difficulty;

      const res = await axios.get(API, { params });
      const d = unwrap(res);
      setCourses(toArray(d?.data ?? d));
      setTotal(typeof d?.total === "number" ? d.total : 0);
      setFacets(d?.facets ?? {});
    } finally { setLoading(false); }
  }, [studentId]);

  const fetchSidePanels = useCallback(async () => {
    if (!studentId) return;
    const [rec, rv, bm] = await Promise.all([
      axios.get(`${API}/recommended/${studentId}`),
      axios.get(`${API}/recently-viewed/${studentId}`),
      axios.get(`${API}/bookmarks/${studentId}`),
    ]);
    setRecommended(toArray(unwrap(rec)));
    setRecentlyViewed(toArray(unwrap(rv)));
    setBookmarks(toArray(unwrap(bm)));
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    fetchCourses();
    fetchSidePanels();
  }, [studentId]);

  const applyFilters = (f: typeof filters) => {
    setFilters(f); setPage(0); fetchCourses(f, 0);
  };

  const enroll = async (courseId: string) => {
    await axios.post(`${API}/enroll`, buildCourseEnrollmentPayload(user, courseId));
    fetchCourses(filters, page);
    fetchSidePanels();
  };

  const toggleBookmark = async (courseId: string) => {
    let previousBookmarked = false;
    let courseSnapshot: any = null;

    setCourses((prev) => prev.map((c) => {
      if ((c.id ?? c._id) !== courseId) return c;
      previousBookmarked = !!c.bookmarked;
      courseSnapshot = c;
      return { ...c, bookmarked: !c.bookmarked };
    }));

    try {
      const res = await axios.post(`${API}/bookmark/toggle`, { studentId, courseId });
      const bookmarked = unwrap(res)?.bookmarked ?? !previousBookmarked;
      setCourses((prev) => prev.map((c) => ((c.id ?? c._id) === courseId ? { ...c, bookmarked } : c)));
      setBookmarks((prev) => {
        if (bookmarked) {
          if (!courseSnapshot || prev.some((b) => (b.id ?? b._id) === courseId)) return prev;
          return [...prev, { ...courseSnapshot, bookmarked: true }];
        }
        return prev.filter((b) => (b.id ?? b._id) !== courseId);
      });
    } catch {
      setCourses((prev) => prev.map((c) => ((c.id ?? c._id) === courseId ? { ...c, bookmarked: previousBookmarked } : c)));
      throw new Error("Failed to toggle bookmark");
    }
  };

  const changePage = (p: number) => { setPage(p); fetchCourses(filters, p); };

  return {
    studentId, courses, facets, total, loading,
    recommended, recentlyViewed, bookmarks,
    filters, applyFilters, page, changePage,
    enroll, toggleBookmark, refetch: () => { fetchCourses(filters, page); fetchSidePanels(); },
  };
}

export async function fetchCourseDetail(courseId: string, studentId: string) {
  const res = await axios.get(`${API}/${courseId}`, { params: { studentId } });
  return res?.data?.result ?? res?.data;
}

export async function fetchLessonDetail(courseId: string, lessonId: string, studentId: string) {
  const res = await axios.get(`${API}/${courseId}/lesson/${lessonId}`, { params: { studentId } });
  return res?.data?.result ?? res?.data;
}

export async function completeLesson(payload: { studentId: string; lessonId: string; courseId: string; quizScore?: number; quizTotal?: number; codingPassed?: boolean }) {
  const res = await axios.post(`${API}/complete-lesson`, payload);
  return res?.data?.result ?? res?.data;
}
